// src/pages/AlmuerzoMes.js
import React, { useEffect, useMemo, useState } from 'react'
import { api } from './api/casinoApi'
import { useNavigate, useLocation } from 'react-router-dom'
import './casino.css'

function ymNow() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function parseYMD(dateStr) {
  const [y, m, d] = dateStr.split('-').map((x) => Number(x))
  const dt = new Date(y, (m || 1) - 1, d || 1)
  dt.setHours(0, 0, 0, 0)
  return dt
}

function addDays(date, n) {
  const d = new Date(date.getTime())
  d.setDate(d.getDate() + n)
  d.setHours(0, 0, 0, 0)
  return d
}

function startOfTodayLocal() {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const LOCK_DAYS = 5

// ✅ Campos que vienen desde getMenuByMonth.php (puedes agregar más si aparecen)
const MENU_FIELDS = [
  { key: 'fondo1', label: 'FONDO 1' },
  { key: 'fondo2', label: 'FONDO 2' },
  { key: 'hipocalorico', label: 'HIPOCALÓRICO' },
  { key: 'vegetariano', label: 'VEGETARIANO' },
  { key: 'otro', label: 'OTRO' },
]

// Limpia texto (NULL, null, undefined, vacío)
const normText = (v) => {
  if (v == null) return ''
  const s = String(v).trim()
  if (!s) return ''
  if (s.toUpperCase() === 'NULL') return ''
  return s
}

// Opciones dinámicas por fila
const buildOptions = (row) => {
  const opts = []
  for (const f of MENU_FIELDS) {
    const val = normText(row?.[f.key])
    if (!val) continue
    opts.push({ key: f.key, label: f.label, value: val })
  }

  // quitar duplicados por value (por si dos campos traen el mismo nombre)
  const seen = new Set()
  return opts.filter((o) => {
    const k = o.value.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

export default function AlmuerzosMes() {
  const nav = useNavigate()
  const location = useLocation()

  const [ym, setYm] = useState(ymNow())
  const [menuRows, setMenuRows] = useState([])
  const [selMap, setSelMap] = useState({})
  const [draftMap, setDraftMap] = useState({})
  const [loading, setLoading] = useState(false)
  const [savingDate, setSavingDate] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')

  const rut = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const rutQuery = (params.get('rut') || '').trim()
    const rutLocal = (localStorage.getItem('rut_almuerzo') || '').trim()
    if (rutQuery) localStorage.setItem('rut_almuerzo', rutQuery)
    return rutQuery || rutLocal
  }, [location.search])

  useEffect(() => {
    if (!rut) nav('/login', { replace: true })
  }, [rut, nav])

  const today = useMemo(() => startOfTodayLocal(), [])
  const cutoff = useMemo(() => addDays(today, LOCK_DAYS), [today])
  const earliestAllowed = useMemo(() => addDays(today, LOCK_DAYS + 1), [today])

  const isSameMonth = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

  // ✅ regla con excepciones (cambio de mes / primeros días del mes)
  const canEditDate = (fechaStr) => {
    const d = parseYMD(fechaStr)
    if (d < today) return false
    if (!isSameMonth(d, today)) return true
    if (today.getDate() <= LOCK_DAYS) return true
    if (d <= cutoff) return false
    return true
  }

  const loadAll = async () => {
    if (!rut) return
    setErr('')
    setMsg('')
    setLoading(true)
    try {
      const [menuRes, selRes] = await Promise.all([
        api.get('/getMenuByMonth.php', { params: { ym } }),
        api.get('/getMySelectionsByMonth.php', { params: { ym, rut } }),
      ])

      if (!menuRes.data?.ok) throw new Error(menuRes.data?.error || 'Error menú')
      if (!selRes.data?.ok) throw new Error(selRes.data?.error || 'Error selecciones')

      const rows = menuRes.data.rows || []
      setMenuRows(rows)

      const map = {}
      for (const r of selRes.data.rows || []) map[r.fecha] = r.almuerzo
      setSelMap(map)

      setDraftMap({})
    } catch (e) {
      const apiErr = e?.response?.data?.error
      setErr(apiErr || 'No se pudo cargar el mes (menú o selecciones).')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym, rut])

  const effectiveChoice = (fecha) => (draftMap[fecha] ?? selMap[fecha] ?? '')

  const setChoice = (fecha, almuerzo) => {
    setDraftMap((prev) => ({ ...prev, [fecha]: almuerzo }))
  }

  const saveOne = async (fecha) => {
    if (!rut) return

    setErr('')
    setMsg('')
    const almuerzo = effectiveChoice(fecha)
    if (!almuerzo) {
      setErr('Selecciona un almuerzo antes de guardar.')
      return
    }

    setSavingDate(fecha)
    try {
      const r = await api.post(
        '/saveMySelection.php',
        { rut, fecha, almuerzo },
        { headers: { 'Content-Type': 'application/json' } }
      )

      if (r.data?.ok) {
        setSelMap((prev) => ({ ...prev, [fecha]: almuerzo }))
        setDraftMap((prev) => {
          const copy = { ...prev }
          delete copy[fecha]
          return copy
        })
        setMsg(`Guardado: ${fecha}`)
      } else {
        setErr(r.data?.error || 'No se pudo guardar.')
      }
    } catch (e) {
      const apiErr = e?.response?.data?.error
      setErr(apiErr || 'Error al guardar.')
    } finally {
      setSavingDate('')
    }
  }

  const logout = () => {
    localStorage.removeItem('rut_almuerzo')
    nav('/login', { replace: true })
  }

  return (
    <div className="casino-wrap">
      <div className="casino-card">
        <div className="casino-row">
          <div>
            <h2 className="casino-title">Programación de Almuerzos</h2>
            <div className="casino-sub">
              <div>
                Rut: <b>{rut || '-'}</b>
              </div>
              <div>
                Bloqueo: no pasado y no {LOCK_DAYS} días próximos (incluye hoy). Permite desde{' '}
                <b>{ymd(earliestAllowed)}</b>.
              </div>
            </div>
          </div>

          <div className="casino-controls">
            <input
              className="casino-input"
              type="month"
              value={ym}
              onChange={(e) => setYm(e.target.value)}
              style={{ minWidth: 170 }}
            />
            <button className="casino-btn" onClick={loadAll} disabled={loading}>
              {loading ? 'Cargando…' : 'Recargar'}
            </button>
            <button className="casino-btn danger" onClick={logout}>
              Salir
            </button>
          </div>
        </div>

        {msg && <div className="casino-alert ok">{msg}</div>}
        {err && <div className="casino-alert err">{err}</div>}

        {loading ? (
          <div className="casino-alert neutral">Cargando…</div>
        ) : menuRows.length === 0 ? (
          <div className="casino-alert err">No hay menú cargado para este mes.</div>
        ) : (
          <div className="casino-table-wrap">
            <table className="casino-table">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Fecha</th>
                  <th>Opciones del día</th>
                  <th style={{ width: 280 }}>Tu elección</th>
                  <th style={{ width: 140 }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {menuRows.map((row) => {
                  const fecha = row.fecha
                  const editable = canEditDate(fecha)
                  const inactive = Number(row.activo) !== 1
                  const disabled = !editable || inactive

                  const chosen = effectiveChoice(fecha)
                  const hasSaved = !!selMap[fecha]
                  const hasDraft = draftMap[fecha] != null && draftMap[fecha] !== selMap[fecha]

                  const options = buildOptions(row)

                  // Si el usuario ya tenía algo guardado, pero ya no viene en el menú,
                  // lo agregamos como opción "no disponible" para que se vea.
                  const chosenNorm = normText(chosen)
                  const existsInOptions = chosenNorm
                    ? options.some((o) => o.value === chosenNorm)
                    : true

                  const selectOptions = existsInOptions
                    ? options
                    : [{ key: '__saved__', label: 'Guardado (no disponible)', value: chosenNorm }, ...options]

                  return (
                    <tr key={fecha} style={disabled ? { opacity: 0.6 } : undefined}>
                      <td>
                        <div>
                          <b>{fecha}</b>
                        </div>
                        <div className="badges">
                          {inactive ? <span className="badge off">No disponible</span> : null}
                          {!editable ? <span className="badge warn">Bloqueado</span> : null}
                          {hasSaved ? (
                            <span className="badge ok">Guardado</span>
                          ) : (
                            <span className="badge neutral">Sin elegir</span>
                          )}
                          {hasDraft ? <span className="badge info">Cambios</span> : null}
                        </div>
                      </td>

                      <td>
                        {options.length === 0 ? (
                          <div className="muted">Sin opciones para este día.</div>
                        ) : (
                          <div style={{ display: 'grid', gap: 6 }}>
                            {options.map((o, idx) => (
                              <div key={o.key}>
                                {idx + 1}) {o.label}: {o.value}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>

                      <td>
                        <select
                          className="casino-select"
                          value={chosenNorm}
                          disabled={disabled || options.length === 0}
                          onChange={(e) => setChoice(fecha, e.target.value)}
                          style={{ width: '100%' }}
                        >
                          <option value="">-- Selecciona --</option>
                          {selectOptions.map((o) => (
                            <option key={`${fecha}-${o.key}-${o.value}`} value={o.value}>
                              {o.label}: {o.value}
                            </option>
                          ))}
                        </select>

                        {disabled ? (
                          <div className="muted">
                            {inactive
                              ? 'Menú deshabilitado.'
                              : 'No se permite editar (pasado o dentro de los próximos 5 días).'}
                          </div>
                        ) : null}
                      </td>

                      <td>
                        <button
                          className="casino-btn primary"
                          disabled={disabled || !chosenNorm || savingDate === fecha}
                          onClick={() => saveOne(fecha)}
                          style={{ width: '100%' }}
                        >
                          {savingDate === fecha ? 'Guardando…' : 'Guardar'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
